<template>
    <div>
        <limitless-page-header>
            <template #header-elements>
                <limitless-page-header-button url="/products/categories" icon="icon-tree6">Trädvy</limitless-page-header-button>
                <limitless-page-header-button url="/products/categories/0" icon="icon-plus3">Ny kategori</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <div v-if="!initialized && busy_loading"><i class="icon-spinner2 spinner"></i></div>
            <div v-else>
                <div class="card">
                    <div class="card-header bg-white header-elements-inline">
                        <h5 class="card-title">Produkter</h5>
                        <div class="header-elements">
                            <span class="text-muted">ProdCatID / ProdSubCatID / PackSortNo</span>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-lg-6">
                                <div class="form-group mb-lg-0">
                                    <label>Huvudkategori</label>
                                    <category-filter
                                        v-model="category_id"
                                        :reduce="(opt) => opt.id"
                                        placeholder="-- Kategori --"
                                    />
                                </div>
                            </div>
                            <div class="col-lg-auto d-flex align-items-end">
                                <button type="button" class="btn btn-light" :disabled="!category_id || busy_products" @click="loadCategory">
                                    <i class="icon-sync mr-1" :class="{ spinner: busy_products }"></i>Hämta
                                </button>
                            </div>
                            <div class="col-lg-auto d-flex align-items-end">
                                <div class="custom-control custom-checkbox mb-2">
                                    <input
                                        type="checkbox"
                                        class="custom-control-input"
                                        id="chk-show-phased-out"
                                        v-model="show_phased_out"
                                    >
                                    <label class="custom-control-label" for="chk-show-phased-out">Visa även produkter som utgått</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="category_id" class="category-board">
                        <div v-if="busy_products" class="p-3"><i class="icon-spinner2 spinner"></i></div>
                        <draggable
                            v-else
                            v-model="sub_categories"
                            handle=".subcategory-handle"
                            class="subcategory-grid"
                        >
                            <div v-for="subCategory in sub_categories" :key="'sub-category-' + subCategory.SubCategoryID" class="subcategory-column">
                                <div class="subcategory-header">
                                    <div>
                                        <div class="font-weight-semibold">{{ subCategoryLabel(subCategory) }}</div>
                                        <span class="badge badge-light">{{ subCategory.products.length }} produkter</span>
                                    </div>
                                    <div v-if="!subCategory.is_uncategorized" class="subcategory-handle"><i class="icon-arrow-resize8"></i></div>
                                </div>

                                <draggable
                                    v-model="subCategory.products"
                                    :group="productDragGroup(subCategory)"
                                    handle=".product-handle"
                                    class="product-list"
                                >
                                    <div
                                        v-for="product in subCategory.products"
                                        :key="'product-' + subCategory.SubCategoryID + '-' + product.ProdID"
                                        class="product-row"
                                        :class="{ 'bg-danger-100': product.isPhasedOut === 1 }"
                                    >
                                        <div v-if="product.images && product.images.length > 0" class="product-thumbnail">
                                            <img
                                                :id="`product-image-${subCategory.SubCategoryID}-${product.ProdID}`"
                                                :src="product.images[0].urls.small"
                                                :alt="product.ProdID"
                                            >
                                            <b-popover :target="`product-image-${subCategory.SubCategoryID}-${product.ProdID}`" triggers="hover">
                                                <img :src="product.images[0].urls.medium" :alt="product.ProdID" class="product-popover-image">
                                            </b-popover>
                                        </div>
                                        <div v-else class="product-thumbnail product-thumbnail-empty"></div>
                                        <div class="product-row-title">
                                            <a :href="'/products/' + product.ProdID + '/web'">#{{ product.ProdID }} - {{ product.ProdNameWeb || product.ProdName }}</a>
                                            <div class="product-badges">
                                                <div v-if="product.isStruct" class="badge badge-info badge-mini">STRUKTURARTIKEL</div>
                                                <div v-if="product.isHiddenFromAllCards" class="badge badge-purple badge-mini">GÖMD FRÅN KORT</div>
                                                <div v-else-if="product.show_on_all_cards" class="badge badge-warning badge-mini">VISAS PÅ ALLA KORT</div>
                                                <div v-if="product.isPhasedOut === 1" class="badge badge-danger badge-mini">HAR UTGÅTT</div>
                                                <template v-else>
                                                    <div v-if="product.isNonStockItem" class="badge badge-warning badge-mini">Beställningsvara</div>
                                                    <template v-if="product.isOutOfStock === 1">
                                                        <div class="badge badge-danger badge-mini">TILLF. SLUT</div>
                                                        <div v-if="product.BackInStockDate && product.BackInStockDate !== '0000-00-00'" class="badge badge-light badge-mini">Åter {{ product.BackInStockDate }}</div>
                                                        <div v-else-if="product.no_refill" class="badge badge-purple badge-mini">EJ REFILL</div>
                                                        <div v-else-if="product.autoblockfunction === 'isPhasedOut'" class="badge badge-warning badge-mini">UTGÅENDE</div>
                                                    </template>
                                                    <div v-else-if="product.no_refill" class="badge badge-purple badge-mini">EJ REFILL</div>
                                                    <div v-else-if="product.autoblockfunction === 'isPhasedOut'" class="badge badge-warning badge-mini">UTGÅENDE</div>
                                                </template>
                                            </div>
<!--                                            <span class="text-muted d-block">{{ productCategoryText(product, subCategory) }}</span>-->
                                        </div>
                                        <span class="badge badge-light mr-2">{{ product.PackSortNo || '-' }}</span>
                                        <div class="product-handle"><i class="icon-arrow-resize8"></i></div>
                                    </div>
                                </draggable>
                                <div v-if="subCategory.products.length === 0" class="empty-list">Inga produkter</div>
                            </div>
                        </draggable>
                    </div>
                </div>

                <div class="d-flex justify-content-end">
                    <limitless-button ref="btnsavesort" @click="saveSort" icon="icon-floppy-disk" :busy="busy_saving" :disabled="!canSaveProducts">SPARA</limitless-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import CategoryFilter from '@components/Filters/CategoryFilter.vue';
import apiClient from '@services/ApiClient';
import draggable from 'vuedraggable';

export default {
    name: 'ProductCategorySorter',
    components: {
        CategoryFilter,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        draggable,
    },
    data: () => ({
        initialized: false,
        busy_loading: true,
        busy_products: false,
        busy_saving: false,
        category_id: null,
        show_phased_out: false,
        sub_categories: [],
    }),
    mounted() {
        this.initialized = true;
        this.busy_loading = false;
    },
    methods: {
        async loadCategory() {
            if (!this.category_id) {
                this.sub_categories = [];
                return;
            }

            const categoryId = this.category_id;
            this.busy_products = true;

            await apiClient.get('/vue/product_sub_categories?sort_by_column=SubCategorySortno&sort_by_order=asc&per_page=10000')
                .then(async (response) => {
                    if (parseInt(categoryId, 10) !== parseInt(this.category_id, 10)) {
                        return;
                    }

                    const subCategories = response.data && response.data.data ? response.data.data : [];
                    this.sub_categories = subCategories
                        .filter((subCategory) => parseInt(subCategory.MainCategoryID, 10) === parseInt(categoryId, 10))
                        .map((subCategory) => ({
                            ...subCategory,
                            is_uncategorized: false,
                            products: [],
                        }));

                    if (this.sub_categories.length === 0) {
                        this.sub_categories = [this.getUncategorizedSubCategory()];
                    } else {
                        this.sub_categories.push(this.getUncategorizedSubCategory());
                    }

                    await Promise.all(this.sub_categories.map((subCategory) => {
                        if (subCategory.is_uncategorized) {
                            return this.loadUncategorizedProducts(subCategory, categoryId);
                        }
                        return this.loadProducts(subCategory, categoryId);
                    }));
                })
                .finally(() => {
                    if (parseInt(categoryId, 10) === parseInt(this.category_id, 10)) {
                        this.busy_products = false;
                    }
                });
        },
        async loadProducts(subCategory, categoryId) {
            const params = new URLSearchParams({
                mode: 'prodlist',
                per_page: 10000,
                sort_by_column: 'PackSortNo',
                sort_by_order: 'asc',
                category_id: categoryId,
                sub_category_id: subCategory.SubCategoryID,
            });
            if (!this.show_phased_out) {
                params.append('is_phased_out', 0);
            }

            await apiClient.get('/vue/products?' + params.toString())
                .then((response) => {
                    if (parseInt(categoryId, 10) !== parseInt(this.category_id, 10)) {
                        return;
                    }

                    subCategory.products = response.data && response.data.data ? response.data.data : [];
                });
        },
        async loadUncategorizedProducts(subCategory, categoryId) {
            const validSubCategoryIds = this.getValidSubCategoryIds();
            const params = new URLSearchParams({
                mode: 'prodlist',
                per_page: 10000,
                sort_by_column: 'PackSortNo',
                sort_by_order: 'asc',
                category_id: categoryId,
            });
            if (!this.show_phased_out) {
                params.append('is_phased_out', 0);
            }

            await apiClient.get('/vue/products?' + params.toString())
                .then((response) => {
                    if (parseInt(categoryId, 10) !== parseInt(this.category_id, 10)) {
                        return;
                    }

                    const products = response.data && response.data.data ? response.data.data : [];
                    subCategory.products = products.filter((product) => !validSubCategoryIds.includes(parseInt(product.ProdSubCatID, 10)));
                });
        },
        getUncategorizedSubCategory() {
            return {
                SubCategoryID: 'none',
                SubCategoryName: 'Ingen kategori',
                MainCategoryID: this.category_id,
                is_uncategorized: true,
                products: [],
            };
        },
        getValidSubCategoryIds() {
            return this.sub_categories
                .filter((subCategory) => !subCategory.is_uncategorized)
                .map((subCategory) => parseInt(subCategory.SubCategoryID, 10));
        },
        subCategoryLabel(subCategory) {
            if (subCategory.is_uncategorized) {
                return subCategory.SubCategoryName;
            }

            return '#' + subCategory.SubCategoryID + ' - ' + subCategory.SubCategoryName;
        },
        productDragGroup(subCategory) {
            if (subCategory.is_uncategorized) {
                return {
                    name: 'products',
                    pull: true,
                    put: false,
                };
            }

            return 'products';
        },
        isValidSubCategoryId(subCategoryId) {
            return this.getValidSubCategoryIds().includes(parseInt(subCategoryId, 10));
        },
        productCategoryText(product, subCategory) {
            if (!subCategory.is_uncategorized) {
                return product.CategoryName + ' / ' + subCategory.SubCategoryName;
            }
            if (!product.ProdSubCatID || parseInt(product.ProdSubCatID, 10) === 0) {
                return product.CategoryName + ' / Ingen kategori';
            }
            if (this.isValidSubCategoryId(product.ProdSubCatID) && product.SubCategoryName) {
                return product.CategoryName + ' / ' + product.SubCategoryName;
            }

            return product.CategoryName + ' / Felaktig underkategori #' + product.ProdSubCatID;
        },
        getProductSortData() {
            const products = [];
            this.sub_categories.forEach((subCategory) => {
                if (subCategory.is_uncategorized) {
                    return;
                }

                subCategory.products.forEach((product, index) => {
                    products.push({
                        ProdID: product.ProdID,
                        ProdCatID: this.category_id,
                        ProdSubCatID: subCategory.SubCategoryID,
                        PackSortNo: parseInt(index, 10) + 100,
                    });
                });
            });

            return products;
        },
        getSubCategorySortData() {
            let sort = 1;
            return this.sub_categories
                .filter((subCategory) => !subCategory.is_uncategorized)
                .map((subCategory) => {
                    const data = {
                        SubCategoryID: subCategory.SubCategoryID,
                        SubCategorySortno: sort,
                        MainCategoryID: this.category_id,
                    };
                    // eslint-disable-next-line no-plusplus
                    sort++;
                    return data;
                });
        },
        async saveSort() {
            if (!this.canSaveProducts) {
                this.$refs.btnsavesort.triggerFailed();
                return;
            }

            this.busy_saving = true;

            await apiClient.post('/vue/product_categories/sort', {
                sub_categories: this.getSubCategorySortData(),
                products: this.getProductSortData(),
            })
                .then(() => {
                    this.$refs.btnsavesort.triggerSaved();
                    this.loadCategory();
                })
                .catch((error) => {
                    if (error.response) {
                        let message = 'Valideringsfel... ';
                        if (error.response.data && error.response.data.message) {
                            message += error.response.data.message;
                        }
                        this.$swal({
                            icon: 'error',
                            title: 'Error!',
                            text: message,
                            reverseButtons: true,
                            confirmButtonText: 'OK',
                            cancelButtonText: 'Avbryt',
                        });
                    }
                    this.$refs.btnsavesort.triggerFailed();
                })
                .finally(() => {
                    this.busy_saving = false;
                });
        },
    },
    watch: {
        category_id() {
            this.sub_categories = [];
            this.loadCategory();
        },
        show_phased_out() {
            if (this.category_id) {
                this.loadCategory();
            }
        },
    },
    computed: {
        canSaveProducts() {
            return !!this.category_id && this.sub_categories.filter((subCategory) => !subCategory.is_uncategorized).length > 0;
        },
    },
};
</script>

<style scoped lang="scss">
.category-board {
    border-top: 1px solid #ddd;
    padding: 12px;
}

.subcategory-grid {
    align-items: stretch;
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
}

.subcategory-column {
    border: 1px solid #ddd;
    border-radius: 4px;
    min-height: 360px;
    min-width: 0;
}

.subcategory-header {
    align-items: center;
    background: #fafafa;
    border-bottom: 1px solid #ddd;
    display: flex;
    justify-content: space-between;
    min-height: 54px;
    padding: 8px 10px;
}

.product-list {
    min-height: 280px;
}

.product-row {
    align-items: center;
    border-bottom: 1px solid #eee;
    display: flex;
    min-height: 72px;
    padding: 8px 10px;
}

.product-row-title {
    flex: 1;
    min-width: 0;
}

.product-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 4px;
    margin-top: 2px;
}

.badge-mini {
    font-size: 9px;
    line-height: 1.2;
    padding: 2px 4px;
}

.product-thumbnail {
    align-items: center;
    display: flex;
    flex: 0 0 56px;
    height: 56px;
    justify-content: center;
    margin-right: 10px;
}

.product-thumbnail img {
    max-height: 52px;
    max-width: 52px;
}

.product-thumbnail-empty {
    background: #f7f7f7;
    border: 1px solid #eee;
}

.product-popover-image {
    width: 100%;
}

.empty-list {
    color: #999;
    padding: 20px 10px;
    text-align: center;
}

.sortable-ghost {
    border: 1px dashed #aaa;
    box-shadow: none;
    position: relative;
}

.sortable-ghost:before {
    background-color: #ffffff;
    bottom: 0;
    content: '';
    display: block;
    left: 0;
    padding: 5px;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 10;
}

.product-handle,
.subcategory-handle {
    cursor: pointer;
    margin-left: auto;
    padding-left: 12px;
}

.subcategory-handle {
    font-size: 18px;
}
</style>
